"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { Cart, CartLineInput } from "@/lib/shopify/types";
import { addBoxAction, addLinesAction, removeLinesAction, updateLineAction, type CartResult } from "./actions";

type CartContextValue = {
  cart: Cart | null;
  // False until the first load from /api/cart has finished.
  ready: boolean;
  pending: boolean;
  error: string | null;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  addLines: (lines: CartLineInput[], options?: { openCart?: boolean }) => Promise<boolean>;
  // One of our gift boxes, by collection handle.
  addBox: (handle: string, message?: string) => Promise<boolean>;
  updateLine: (lineId: string, quantity: number) => Promise<boolean>;
  removeLines: (lineIds: string[]) => Promise<boolean>;
};

const CartContext = createContext<CartContextValue | null>(null);

export function useCart(): CartContextValue {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside <CartProvider>.");
  return value;
}

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [inFlight, setInFlight] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    fetch("/api/cart", { cache: "no-store" })
      .then((res) => res.json())
      .then((body: { cart: Cart | null }) => mounted.current && setCart(body.cart ?? null))
      .catch(() => undefined)
      .finally(() => mounted.current && setReady(true));
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(async (action: () => Promise<CartResult>): Promise<boolean> => {
    setInFlight((n) => n + 1);
    setError(null);
    try {
      const result = await action();
      if (result.cart !== undefined) setCart(result.cart);
      setError(result.ok ? (result.notice ?? null) : result.error);
      return result.ok;
    } catch {
      setError("We couldn't reach the shop. Check your connection and try again.");
      return false;
    } finally {
      setInFlight((n) => n - 1);
    }
  }, []);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      ready,
      pending: inFlight > 0,
      error,
      isOpen,
      open: () => setIsOpen(true),
      close: () => {
        setIsOpen(false);
        setError(null);
      },
      addLines: async (lines, { openCart = true } = {}) => {
        const ok = await run(() => addLinesAction(lines));
        if (ok && openCart) setIsOpen(true);
        return ok;
      },
      addBox: async (handle, message) => {
        const ok = await run(() => addBoxAction(handle, message));
        if (ok) setIsOpen(true);
        return ok;
      },
      updateLine: (lineId, quantity) => run(() => updateLineAction(lineId, quantity)),
      removeLines: (lineIds) => run(() => removeLinesAction(lineIds)),
    }),
    [cart, ready, inFlight, error, isOpen, run],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}
